import { trackEvent } from '@/api/track';
import { useUserStore } from '@/store/useUserStore';

type CreateTrackingData = Record<string, unknown>;

const normalizeText = (value: unknown) => String(value ?? '').trim();

export const getCreateRoute = () => {
  if (typeof window === 'undefined') return '';
  return `${window.location.pathname}${window.location.search}`;
};

export const getCreateEntrySource = (fallback = 'create_page') => {
  if (typeof window === 'undefined') return fallback;
  const params = new URLSearchParams(window.location.search);
  return (
    normalizeText(params.get('source')) ||
    normalizeText(params.get('entry_source')) ||
    normalizeText(params.get('entry')) ||
    fallback
  );
};

export const getPromptLengthBucket = (length: number) => {
  if (length <= 0) return '0';
  if (length < 20) return '1-19';
  if (length < 100) return '20-99';
  if (length < 300) return '100-299';
  if (length <= 600) return '300-600';
  return '600+';
};

export const getUrlHost = (value: string) => {
  const text = value.trim();
  if (!text) return '';
  try {
    const url = new URL(text.startsWith('http') ? text : `https://${text}`);
    return url.hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

export const getFileExtension = (fileName: string) => {
  const match = fileName
    .trim()
    .toLowerCase()
    .match(/\.([a-z0-9]+)$/);
  return match?.[1] ?? '';
};

export const getAttachedSourceCount = (websiteUrl: string, brandPdfName: string) =>
  [websiteUrl, brandPdfName].filter((value) => value.trim()).length;

export const buildCreateInputContext = ({
  route = getCreateRoute(),
  entrySource,
  prompt,
  websiteUrl,
  brandPdfName,
}: {
  route?: string;
  entrySource?: string;
  prompt: string;
  websiteUrl: string;
  brandPdfName: string;
}) => ({
  route,
  entry_source: entrySource || getCreateEntrySource(),
  prompt_length: prompt.length,
  has_website_url: Boolean(websiteUrl.trim()),
  has_brand_pdf: Boolean(brandPdfName.trim()),
  is_authenticated: useUserStore.getState().isAuthenticated,
});

export const trackCreateEvent = (eventCode: string, data: CreateTrackingData = {}) => {
  trackEvent({
    eventCode,
    eventData: JSON.stringify({
      route: getCreateRoute(),
      entry_source: getCreateEntrySource(),
      is_authenticated: useUserStore.getState().isAuthenticated,
      ...data,
    }),
  });
};
